import type { PlayerRef, TPlayer } from '@setplex/pureya'
import type { MediaUrl, SeriesPlaylist } from '@setplex/tria-api'
import type { ICWatching } from '~/entities/player/show/continue-watching'

import { Events } from '@setplex/pureya'
import { api } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useEffect } from 'react'
import { $vodFormatted } from '~/entities/movies/model'
import { showCWModel, vodCWModel } from '~/entities/player'
import { playlistModel } from '~/entities/player/show'
import { formatPlaylistToCW } from '~/entities/player/show/playlist/lib'
import { CONTENT_TYPE } from '~/shared/constants/content'
import { ONE_MINUTE_MS, ONE_SECOND_MS } from '~/shared/constants/time'

type UseLastPositionSaveProps = {
  player: {
    instance?: TPlayer
    events: {
      on?: PlayerRef['on']
      off?: PlayerRef['off']
    }
  }
  contentType?: CONTENT_TYPE
  isTrailer?: boolean
  stoppedTime?: number // override common value with custom one
}

export const useLastPositionSave = ({
  player,
  contentType = CONTENT_TYPE.VOD,
  isTrailer,
  stoppedTime,
}: UseLastPositionSaveProps) => {
  const setContinueWatchingShow = useUnit(showCWModel.updateTime)
  const setContinueWatchingVod = useUnit(vodCWModel.updateTime)

  const cwBeforeRedirect = useUnit(playlistModel.$cwBeforeRedirect)

  const seriesPlaylist: SeriesPlaylist | null =
    useUnit(api.tvshows.$seriesPlaylist) ?? null

  const currentVod = useUnit($vodFormatted) ?? null

  const currentPlayerPageUrl: MediaUrl | null =
    useUnit(api.player.$currentPlayerPageUrl) ?? null

  player.events?.on?.('loadedmetadata', () => {
    let savedStoppedTime

    if (typeof stoppedTime === 'number') savedStoppedTime = stoppedTime
    else {
      if (typeof currentPlayerPageUrl?.stoppedTime === 'number')
        savedStoppedTime = currentPlayerPageUrl?.stoppedTime / ONE_SECOND_MS
    }

    if (!Number.isFinite(savedStoppedTime) || savedStoppedTime == null) return

    player.instance?.currentTime(savedStoppedTime)
  })

  const savePositionVod = useCallback(
    (stoppedTime: number, playerLengthMillis: number) => {
      if (!currentVod) return

      setContinueWatchingVod({
        content: currentVod,
        stoppedTime,
        lengthMillis: currentVod.duration || playerLengthMillis,
      })
    },
    [currentVod, setContinueWatchingVod]
  )

  const savePositionShow = useCallback(
    (stoppedTime: number, playerLengthMillis: number, content?: ICWatching) => {
      const cw = content ?? formatPlaylistToCW(seriesPlaylist, stoppedTime)

      if (!cw || !cw.episodeId || !cw.tvShow) return

      // `playerLengthMillis` reflects the actively loaded source, so it's only
      // a safe fallback when describing that episode. `content` (redirect from
      // $cwBeforeRedirect) can describe a previous episode we've already
      // switched away from — substituting fresh player duration there would
      // misattribute the next episode's length to it.
      const lengthMillis = content
        ? cw.lengthMillis || 0
        : cw.lengthMillis || playerLengthMillis

      setContinueWatchingShow({
        ...cw,
        stoppedTime,
        lengthMillis,
      })
    },
    [seriesPlaylist, setContinueWatchingShow]
  )

  const saveLatestPosition = useCallback(
    (content?: ICWatching) => {
      const stoppedTime = Math.round(
        (player.instance?.currentTime() || 0) * ONE_SECOND_MS
      )

      if (stoppedTime <= ONE_MINUTE_MS || isTrailer) return

      const rawDuration = player.instance?.duration?.()
      const playerLengthMillis =
        typeof rawDuration === 'number' &&
        Number.isFinite(rawDuration) &&
        rawDuration > 0
          ? Math.round(rawDuration * ONE_SECOND_MS)
          : 0

      if (contentType === CONTENT_TYPE.VOD)
        return savePositionVod(stoppedTime, playerLengthMillis)

      return savePositionShow(stoppedTime, playerLengthMillis, content)
    },
    [contentType, isTrailer, savePositionVod, savePositionShow, player.instance]
  )

  useEffect(() => {
    // Update CW data when content switched through playlist
    if (cwBeforeRedirect) saveLatestPosition(cwBeforeRedirect)
  }, [saveLatestPosition, cwBeforeRedirect])

  useEffect(() => {
    // Save on player dispose
    player.events?.on?.('dispose', saveLatestPosition)

    player.events?.on?.(Events.Player.PlayNext, saveLatestPosition)
    player.events?.on?.(Events.Player.PlayNextEpisode, saveLatestPosition)
    player.events?.on?.(Events.Player.PlayPrevious, saveLatestPosition)

    // Save on tab close
    const handleCloseTabEvent = () => {
      if (document.visibilityState === 'hidden') saveLatestPosition()
    }

    document.addEventListener('visibilitychange', handleCloseTabEvent)
    return () => {
      document.removeEventListener('visibilitychange', handleCloseTabEvent)
    }
  }, [saveLatestPosition, player])
}
